import { useNavigate } from "react-router-dom";
import {
  Settings,
  ChevronRight,
  Bell,
  User,
  Shield,
  Palette,
  Building2,
} from "lucide-react";
import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { notifyFeature } from "@/utils/demoNotifications";
import { useDemoOrg } from "@/contexts/DemoOrgContext";
import { Widget } from "./Widget";
import type { DragHandleProps } from "../../constants";

export const SettingsWidget = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => {
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const demoOrgContext = useDemoOrg();
  const orgId = demoOrgContext?.selectedOrg?.orgId || "";

  const settingsItems = [
    {
      icon: <User className="size-4" aria-hidden="true" />,
      label: "Profile",
      description: "Manage your account details",
      path: isDemo ? PATH_ROOT.demo.profile : PATH_ROOT.main.profile,
    },
    {
      icon: <Building2 className="size-4" aria-hidden="true" />,
      label: "Organization",
      description: "Manage your organization",
      path: isDemo
        ? PATH_ROOT.demo.organizationManagement(orgId)
        : PATH_ROOT.organization.management,
    },
    {
      icon: <Shield className="size-4" aria-hidden="true" />,
      label: "Security",
      description: "Password and authentication",
      path: null,
    },
    {
      icon: <Bell className="size-4" aria-hidden="true" />,
      label: "Notifications",
      description: "Email and push preferences",
      path: null,
    },
    {
      icon: <Palette className="size-4" aria-hidden="true" />,
      label: "Appearance",
      description: "Theme and display options",
      path: null,
    },
  ];

  return (
    <Widget
      title="Settings"
      icon={<Settings className="size-5" aria-hidden="true" />}
      linkTo={isDemo ? PATH_ROOT.demo.profile : PATH_ROOT.main.profile}
      linkText="All settings"
      dragHandleProps={dragHandleProps}
    >
      <div className="flex flex-col gap-1">
        {settingsItems.map((item, index) => (
          <button
            key={index}
            onClick={() => (item.path ? navigate(item.path) : notifyFeature(item.label, true))}
            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="w-8 h-8 rounded-full bg-muted grid place-items-center text-muted-foreground shrink-0">
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-foreground text-sm font-medium">{item.label}</div>
              <div className="text-muted-foreground text-xs mt-0.5">{item.description}</div>
            </div>
            <ChevronRight className="size-4 text-muted-foreground/50 shrink-0" aria-hidden="true" />
          </button>
        ))}
      </div>
    </Widget>
  );
};
